import React from 'react';

/**
 * Props for `FieldSection`.
 *
 * @property title - Optional header text shown above the content.
 * @property children - The section's content. Rendered inside a container with vertical dividers.
 * @property className - Additional Tailwind classes applied to the root container.
 */
type Props = {
  title?: string;
  children: React.ReactNode;
  className?: string;
};

/**
 * FieldSection
 *
 * Presentational wrapper used to group related form fields or content in the claims-filing UI.
 * It renders an optional header row and a bordered, rounded, white container. The body applies
 * Tailwind's `divide-y` to visually separate each child as its own row/section.
 *
 * Structure:
 * - Root: `border rounded-lg bg-white` (+ any `className` passed).
 * - Header (optional): small, bold title inside a top bar with bottom border.
 * - Body: children with vertical dividers between them.
 *
 * Accessibility: purely presentational—provide semantics/labels within children as needed.
 *
 * @example
 * ```tsx
 * <FieldSection title="Insured Details" className="mt-4">
 *   <InsuredNameRow />
 *   <InsuredContactRow />
 * </FieldSection>
 * ```
 */
export const FieldSection: React.FC<Props> = ({
  title,
  children,
  className,
}) => {
  return (
    <div className={`border rounded-lg bg-white ${className || ''}`}>
      {title && (
        <div className="px-3 py-2 border-b">
          <div className="text-sm font-semibold">{title}</div>
        </div>
      )}
      <div className="divide-y">{children}</div>
    </div>
  );
};
